import { AxiosError, AxiosResponse } from 'axios';
import { getApi } from 'api';
import { MinorBreakRule } from 'constants/MinorBreakRule';
import { MinorNightWorkRule } from 'constants/MinorNightWorkRule';
import { ScopeLevel } from 'constants/ScopeLevel';
import {
  ContractComplianceRule,
  IAddRuleExceptions,
  IJustificationDocument,
  IMinorBreakRuleDefault,
  IMinorNightWorkRuleDefault,
  IPagedResult,
  IRemoveRuleException,
  IRuleException,
  ISaveMinorBreakDefault,
  ISaveMinorNightWorkDefault,
  IScopeEntity,
  IUpdateRuleException,
  ParsedContractComplianceError,
} from 'types/contract-compliance/IContractCompliance';

const BASE_URL = 'ContractCompliance';
const MINOR_BREAKS_URL = `${BASE_URL}/MinorBreaks`;
const MINOR_NIGHT_WORK_URL = `${BASE_URL}/MinorNightWork`;

/** The shared axios instance has no timeout; the "side panel fails to load" AC needs one (same as feature access). */
export const CONTRACT_COMPLIANCE_REQUEST_TIMEOUT_MS = 30000;
const config = { timeout: CONTRACT_COMPLIANCE_REQUEST_TIMEOUT_MS };

interface ContractComplianceErrorBody {
  error?: unknown;
  message?: unknown;
  details?: Record<string, unknown>;
}

/** `message` is already translated by the API; `error` is the key to fall back on. */
export const parseContractComplianceError = (error: unknown): ParsedContractComplianceError => {
  const response = (error as AxiosError<ContractComplianceErrorBody>)?.response;
  const body = response?.data;
  return {
    status: response?.status ?? null,
    key: typeof body?.error === 'string' ? body.error : null,
    message: typeof body?.message === 'string' && body.message.trim() ? body.message : null,
    details: body?.details ?? {},
  };
};

export const getMinorBreakDefaultsRequest = async (): Promise<IMinorBreakRuleDefault[]> => {
  const response: AxiosResponse<IMinorBreakRuleDefault[]> = await getApi().get(`${MINOR_BREAKS_URL}/Defaults`, config);
  return response.data;
};

export const getMinorBreakDefaultRequest = async (rule: MinorBreakRule): Promise<IMinorBreakRuleDefault> => {
  const response: AxiosResponse<IMinorBreakRuleDefault> = await getApi().get(
    `${MINOR_BREAKS_URL}/Defaults/${rule}`,
    config,
  );
  return response.data;
};

export const saveMinorBreakDefaultRequest = async (
  rule: MinorBreakRule,
  body: ISaveMinorBreakDefault,
): Promise<IMinorBreakRuleDefault> => {
  const response: AxiosResponse<IMinorBreakRuleDefault> = await getApi().put(
    `${MINOR_BREAKS_URL}/Defaults/${rule}`,
    body,
    config,
  );
  return response.data;
};

export interface GetRuleExceptionsParams {
  rule: ContractComplianceRule;
  search: string;
  pageIndex: number;
  pageSize: number;
}

export interface RuleExceptionRequests {
  getPage: (params: GetRuleExceptionsParams) => Promise<IPagedResult<IRuleException>>;
  get: (id: number) => Promise<IRuleException>;
  add: (body: IAddRuleExceptions) => Promise<IRuleException[]>;
  update: (id: number, body: IUpdateRuleException) => Promise<IRuleException>;
  remove: (id: number, body: IRemoveRuleException) => Promise<void>;
}

const buildRuleExceptionRequests = (url: string): RuleExceptionRequests => ({
  getPage: async params => {
    const response: AxiosResponse<IPagedResult<IRuleException>> = await getApi().get(`${url}/Exceptions`, {
      ...config,
      params,
    });
    return response.data;
  },
  get: async id => {
    const response: AxiosResponse<IRuleException> = await getApi().get(`${url}/Exceptions/${id}`, config);
    return response.data;
  },
  add: async body => {
    const response: AxiosResponse<IRuleException[]> = await getApi().post(`${url}/Exceptions`, body, config);
    return response.data;
  },
  update: async (id, body) => {
    const response: AxiosResponse<IRuleException> = await getApi().put(`${url}/Exceptions/${id}`, body, config);
    return response.data;
  },
  remove: async (id, body) => {
    await getApi().post(`${url}/Exceptions/${id}/Remove`, body, config);
  },
});

export const minorBreakExceptionRequests = buildRuleExceptionRequests(MINOR_BREAKS_URL);
export const minorNightWorkExceptionRequests = buildRuleExceptionRequests(MINOR_NIGHT_WORK_URL);

export const getMinorNightWorkDefaultsRequest = async (): Promise<IMinorNightWorkRuleDefault[]> => {
  const response: AxiosResponse<IMinorNightWorkRuleDefault[]> = await getApi().get(
    `${MINOR_NIGHT_WORK_URL}/Defaults`,
    config,
  );
  return response.data;
};

export const getMinorNightWorkDefaultRequest = async (
  rule: MinorNightWorkRule,
): Promise<IMinorNightWorkRuleDefault> => {
  const response: AxiosResponse<IMinorNightWorkRuleDefault> = await getApi().get(
    `${MINOR_NIGHT_WORK_URL}/Defaults/${rule}`,
    config,
  );
  return response.data;
};

export const saveMinorNightWorkDefaultRequest = async (
  rule: MinorNightWorkRule,
  body: ISaveMinorNightWorkDefault,
): Promise<IMinorNightWorkRuleDefault> => {
  const response: AxiosResponse<IMinorNightWorkRuleDefault> = await getApi().put(
    `${MINOR_NIGHT_WORK_URL}/Defaults/${rule}`,
    body,
    config,
  );
  return response.data;
};

export const getScopeEntitiesRequest = async (scopeLevel: ScopeLevel): Promise<IScopeEntity[]> => {
  const response: AxiosResponse<IScopeEntity[]> = await getApi().get(`${BASE_URL}/ScopeEntities`, {
    ...config,
    params: { scopeLevel },
  });
  return response.data;
};

/** Uploading does not save anything: the returned id is sent with the save that follows. */
export const uploadJustificationDocumentRequest = async (file: File): Promise<IJustificationDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  const response: AxiosResponse<IJustificationDocument> = await getApi().post(
    `${BASE_URL}/JustificationDocuments`,
    formData,
    { ...config, headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};
